import React, { useState } from 'react';
import { User } from '../../types';
import { LogOut, Users, X, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { chatService } from '../../services/chatService';
import { formatDistanceToNow, format, differenceInMinutes } from 'date-fns';
import { Notepad } from '../Notepad';
import { APP_VERSION } from '../../version';

interface SidebarProps {
  users: User[];
  currentUser: User;
  customStatusText: string;
  setCustomStatusText: (text: string) => void;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ users, currentUser, customStatusText, setCustomStatusText, onClose }) => {
  const { logout } = useAuth();
  const otherUsers = users.filter(u => u.id !== currentUser.id);
  const [isEditingStatus, setIsEditingStatus] = useState(false);

  const isUserOnline = (user: User) => {
    if (!user.lastSeen) return false;
    const minutesSinceLastSeen = differenceInMinutes(new Date(), new Date(user.lastSeen));
    return minutesSinceLastSeen < 60;
  };

  const handleStatusSave = async () => {
    if (customStatusText.length > 100) return;
    await chatService.updateUserCustomStatus(currentUser.id, customStatusText);
    setIsEditingStatus(false);
  };

  const handleClearStatus = async () => {
    await chatService.updateUserCustomStatus(currentUser.id, '');
    setCustomStatusText('');
  }

  return (
    <div className="w-full md:w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={currentUser.photoURL} alt="Me" className="w-10 h-10 rounded-full border-2 border-blue-100" />
          <div>
            <h2 className="font-bold text-gray-800">{currentUser.displayName}</h2>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 ${isUserOnline(currentUser) ? 'bg-green-500' : 'bg-gray-400'} rounded-full`}></span>
              {isEditingStatus ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customStatusText}
                    onChange={(e) => setCustomStatusText(e.target.value)}
                    className="text-xs text-gray-700 bg-gray-100 rounded-md px-2 py-1 w-full"
                    maxLength={100}
                    autoFocus
                  />
                  <button onClick={handleStatusSave} className="text-gray-400 hover:text-green-500">
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs text-gray-500 font-medium flex items-center gap-1 cursor-pointer"
                    onClick={() => setIsEditingStatus(true)}
                  >
                    {customStatusText || 'Set a status'}
                  </span>
                  {customStatusText && (
                    <button onClick={handleClearStatus} className="text-gray-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={logout} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Logout"><LogOut className="w-5 h-5" /></button>
          {onClose && (
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors md:hidden" title="Close"><X className="w-5 h-5" /></button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Users className="w-4 h-4" />Active Members</h3>
        <div className="space-y-2">
          {otherUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-xl border border-dashed border-gray-200"><p>No one else is here yet.</p></div>
          ) : (
            otherUsers.map(user => (
              <div key={user.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group">
                <div className="relative">
                  <img src={user.photoURL} alt={user.displayName} className="w-12 h-12 rounded-full object-cover border border-gray-100 group-hover:border-blue-200 transition-colors" />
                  {user.lastSeen && <span className={`absolute bottom-0 right-0 w-3 h-3 ${isUserOnline(user) ? 'bg-green-500' : 'bg-gray-400'} border-2 border-white rounded-full`}></span>}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{user.displayName}</h4>
                  <p className="text-xs text-gray-500 truncate">{user.customStatus || (user.status === 'online' ? 'Online' : `Last seen ${formatDistanceToNow(user.lastSeen)} ago`)}</p>
                  {user.lastSignInTime && <p className="text-xs text-gray-500 truncate">Last login: {format(user.lastSignInTime, "dd.MM.yyyy HH")}</p>}
                </div>
              </div>
            ))
          )}
        </div>
        <Notepad />
      </div>
      <div className="p-4 bg-gray-50 text-xs text-center text-gray-400 border-t border-gray-100">Exclusive messaging {APP_VERSION} • Secure & Private</div>
    </div>
  );
};
